<template>
  <fieldset
    class="tuition-residency"
    data-testid="residency"
    tabindex="-1"
    :aria-describedby="props.errorId"
  >
    <legend class="text-dark-1">
      {{ props.title || "What is your Arizona residency status?" }}
    </legend>
    <div
      v-if="props.description"
      class="tuition-residency__description"
      data-testid="residency-description"
      role="presentation"
      @click="handleFaqLink"
      v-html="props.description"
    ></div>
    <div class="tuition-residency__options" role="radiogroup">
      <label
        class="tuition-residency__option"
        :class="{
          'tuition-residency__option--selected': props.modelValue === 'res'
        }"
      >
        <input
          class="tuition-residency__input"
          type="radio"
          name="tuition-calculator-residency"
          value="res"
          data-testid="residency-option-res"
          :checked="props.modelValue === 'res'"
          @change="handleSelect('res')"
        />
        <span>{{ residencyLabels.res }}</span>
        <FontAwesomeIcon
          v-if="props.modelValue === 'res'"
          class="tuition-residency__status-icon"
          :icon="faCircleCheck"
          aria-hidden="true"
          data-testid="residency-option-res-selected-icon"
        />
        <span
          v-else
          class="tuition-residency__empty-icon"
          aria-hidden="true"
          data-testid="residency-option-res-empty-icon"
        ></span>
      </label>
      <label
        class="tuition-residency__option"
        :class="{
          'tuition-residency__option--selected': props.modelValue === 'nores'
        }"
      >
        <input
          class="tuition-residency__input"
          type="radio"
          name="tuition-calculator-residency"
          value="nores"
          data-testid="residency-option-nores"
          :checked="props.modelValue === 'nores'"
          @change="handleSelect('nores')"
        />
        <span>{{ residencyLabels.nores }}</span>
        <FontAwesomeIcon
          v-if="props.modelValue === 'nores'"
          class="tuition-residency__status-icon"
          :icon="faCircleCheck"
          aria-hidden="true"
          data-testid="residency-option-nores-selected-icon"
        />
        <span
          v-else
          class="tuition-residency__empty-icon"
          aria-hidden="true"
          data-testid="residency-option-nores-empty-icon"
        ></span>
      </label>
    </div>
    <p v-if="props.error" :id="props.errorId" class="text-danger">
      {{ props.error }}
    </p>
  </fieldset>
</template>

<script setup lang="ts">
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { TUITION_RESIDENCY_LABELS } from "~/src/services/constants";
import type { FaqLinkTarget, TuitionCalculatorResidency } from "~/types/tuition-calculator";
import { isExternalUrl } from "~/utils";

const props = defineProps<{
  modelValue: TuitionCalculatorResidency | null;
  title?: string | null;
  description?: string | null;
  error?: string;
  errorId: string;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: TuitionCalculatorResidency): void;
  (event: "select", label: string): void;
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const residencyLabels = TUITION_RESIDENCY_LABELS;

const handleFaqLink = (event: MouseEvent): void => {
  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor || isExternalUrl(anchor.getAttribute("href") ?? "")) return;
  event.preventDefault();
  emit("faq-link", "residency");
};

const handleSelect = (value: TuitionCalculatorResidency): void => {
  emit("update:modelValue", value);
  emit("select", residencyLabels[value]);
};
</script>

<style scoped lang="scss">
.tuition-residency {
  margin: 2rem 0 0;
  padding: 1.5rem 0 0;
  border: 0;
  border-top: 1px solid var(--rds-border-color, #d8d8d8);
}

.tuition-residency legend {
  float: left;
  width: 100%;
  margin-bottom: 0.75rem;
  padding: 0;
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  color: var(--rds-dark-3, #747474);
}

.tuition-residency__description {
  clear: both;
  max-width: 48rem;
  margin-bottom: 1rem;
  color: var(--rds-dark-3, #4d4d4d);
}

.tuition-residency__options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tuition-residency__option {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 2.75rem;
  padding: 1rem;
  border: 1px solid var(--rds-border-color, #c8c8c8);
  border-radius: 0.25rem;
  color: var(--rds-dark-3, #4d4d4d);
  cursor: pointer;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
}

.tuition-residency__option:not(.tuition-residency__option--selected):hover,
.tuition-residency__option:not(.tuition-residency__option--selected):focus-within {
  background: #f1f1f1;
}

.tuition-residency__option--selected {
  border-color: var(--rds-primary, #8c1d40);
  background: var(--rds-primary, #8c1d40);
  accent-color: var(--rds-primary, #8c1d40);
  color: #fff;
}

.tuition-residency__input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  border: 0;
}

.tuition-residency__empty-icon,
.tuition-residency__status-icon {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
}

.tuition-residency__empty-icon {
  border: 2px solid var(--rds-dark-2, #747474);
  border-radius: 50%;
}

.tuition-residency__status-icon {
  color: #fff;
}

@media (max-width: 575.98px) {
  .tuition-residency__option {
    width: 100%;
  }
}
</style>
